import { FC } from 'react'
import { Skeleton } from 'src/components/Skeleton'
import styles from './sentimentDistributionCard.module.scss'

export interface SentimentBreakdownItem {
  count: number
  percentage: number
}

export interface SentimentDistributionData {
  negative: SentimentBreakdownItem
  neutral: SentimentBreakdownItem
  positive: SentimentBreakdownItem
}

interface SentimentDistributionCardProps {
  className?: string
  data?: SentimentDistributionData
  // Optional: parents that know their query is still in flight pass this so the
  // card shimmers instead of asserting the 0/0% fallback below as a real
  // measurement. Parents that don't pass it keep the previous behaviour.
  isLoading?: boolean
  title?: string
}

// Percent/count sizes come from sentimentDistributionCard.module.scss:
// .percentValue is 14px with min-width 36px, .sentimentNumber is 12px with
// min-width 35px — the skeletons match so the row doesn't reflow on load.
const PERCENT_SKELETON = { height: 14, width: 36 }
const COUNT_SKELETON = { height: 12, width: 35 }

const SentimentRow: FC<{
  barClass: string
  isLoading: boolean
  label: string
  value: SentimentBreakdownItem
}> = ({ barClass, isLoading, label, value }) => (
  <div className={styles.sentimentRow}>
    {/* .barContainer is a fixed 8px-tall track, so a 0%-wide bar while loading
        reserves the same space the real bar will occupy — nothing shifts. */}
    <div className={styles.barContainer}>
      <div
        className={`${styles.bar} ${barClass}`}
        style={{ width: isLoading ? '0%' : `${value.percentage}%` }}
      />
    </div>
    <div className={styles.sentimentInfo}>
      <div className={styles.sentimentText}>
        {isLoading ? (
          <Skeleton
            height={PERCENT_SKELETON.height}
            width={PERCENT_SKELETON.width}
          />
        ) : (
          <span className={styles.percentValue}>{value.percentage}%</span>
        )}
        <span className={styles.sentimentType}>{label}</span>
      </div>
      {isLoading ? (
        <Skeleton height={COUNT_SKELETON.height} width={COUNT_SKELETON.width} />
      ) : (
        <span className={styles.sentimentNumber}>
          {value.count.toLocaleString()}
        </span>
      )}
    </div>
  </div>
)

export const SentimentDistributionCard: FC<SentimentDistributionCardProps> = ({
  className,
  data,
  isLoading = false,
  title = 'Sentiment Distribution',
}) => {
  const positive = data?.positive ?? { count: 0, percentage: 0 }
  const neutral = data?.neutral ?? { count: 0, percentage: 0 }
  const negative = data?.negative ?? { count: 0, percentage: 0 }

  // First load only: once real values are on screen a refetch keeps them.
  const isInitialLoading = isLoading && !data

  return (
    <div className={`${styles.metricCard} ${className ?? ''}`}>
      <div className={styles.cardHeader}>
        <span className={styles.cardTitle}>{title}</span>
      </div>
      <div className={styles.sentimentContent}>
        <SentimentRow
          barClass={styles.positiveBar}
          isLoading={isInitialLoading}
          label="Positive"
          value={positive}
        />
        <SentimentRow
          barClass={styles.neutralBar}
          isLoading={isInitialLoading}
          label="Neutral"
          value={neutral}
        />
        <SentimentRow
          barClass={styles.negativeBar}
          isLoading={isInitialLoading}
          label="Negative"
          value={negative}
        />
      </div>
    </div>
  )
}
